import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { leadSectionTopHomeV2Content$key } from "scmp-app/queries/__generated__/leadSectionTopHomeV2Content.graphql";

import { Container, LeadCoverImage, LeadImageLink, LeadText, StyledLeadItem } from "./styles";

type Props = ContentItemRenderVariantProps & {
  reference: leadSectionTopHomeV2Content$key;
  useWideCoverImage?: boolean;
};

/**
 * The section-block lead story, shared by both widget variants: headline +
 * summary above the cover image on mobile, image-first from tablet up.
 * Variant-specific sizing (the wide variant-one crop, gaps) is applied by the
 * variants via styled overrides on the exported inner elements.
 */
export const HomeV2SectionTopLead: FunctionComponent<Props> = ({
  className,
  onClick,
  reference,
  useWideCoverImage = false,
}) => {
  const content = useFragment(
    graphql`
      fragment leadSectionTopHomeV2Content on Content {
        entityId
        urlAlias
        ...entityLink
        ...homeSecondaryV2ContentItemContent
        ...contentCoverImageContent
          @arguments(withLandscape250x99: true, withSize1200x800: true, withSize540x360: true)
      }
    `,
    reference,
  );

  return (
    <Container className={className}>
      <LeadImageLink
        onClick={() => onClick?.(content.entityId, content.urlAlias)}
        reference={content}
      >
        <LeadCoverImage
          reference={content}
          responsiveVariants={{
            desktopUp: useWideCoverImage ? "landscape250x99" : "size1200x800",
            mobileUp: "size540x360",
            tabletUp: useWideCoverImage ? "landscape250x99" : "size1200x800",
          }}
        />
      </LeadImageLink>
      <LeadText>
        <StyledLeadItem
          isReadingTimeEnabled
          onClick={onClick}
          reference={content}
          withComment
          withLiveTag
          withSummary
        />
      </LeadText>
    </Container>
  );
};

HomeV2SectionTopLead.displayName = "HomeV2SectionTopLead";
